import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-95",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-md hover:bg-primary/90 hover:scale-105 hover:shadow-lg",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:scale-105",
        outline:
          "border-2 border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground hover:border-primary/50",
        "outline-inverted":
          "border-2 border-primary-foreground bg-transparent text-primary-foreground shadow-sm hover:bg-primary-foreground hover:text-secondary hover:scale-105 hover:shadow-lg",
        "outline-light":
          "border-2 border-primary-foreground/80 bg-transparent text-primary-foreground font-semibold shadow-sm hover:border-secondary hover:bg-primary-foreground hover:text-secondary hover:scale-105 hover:shadow-xl transition-all duration-300",
        secondary: "bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90 hover:scale-105 hover:shadow-md",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        "hero-primary": "bg-secondary text-secondary-foreground font-bold shadow-lg hover:bg-secondary/90 hover:scale-105 hover:shadow-xl",
        "hero-secondary": "border-2 border-white bg-white/10 text-white font-semibold shadow-lg backdrop-blur-sm hover:bg-white/20 hover:scale-105",
        "sticky-cta": "bg-secondary text-secondary-foreground font-bold shadow-lg hover:bg-secondary/90 hover:scale-105 hover:shadow-xl",
        "sticky-secondary": "border-2 border-white bg-white/15 text-white font-semibold shadow-sm backdrop-blur-sm hover:bg-white/25 hover:border-white",
      },
      size: {
        default: "h-11 px-4 py-2 min-h-[44px]", // 44px minimum for touch targets
        sm: "h-10 rounded-md px-3 text-sm min-h-[44px]", // 40px height but 44px min-height
        lg: "h-12 rounded-md px-8 text-base", // Already meets 48px
        xl: "h-14 rounded-md px-10 text-lg", // Already meets 56px
        "2xl": "h-auto rounded-md px-8 py-6 text-lg", // Custom large size for CTAs
        icon: "h-11 w-11 min-h-[44px] min-w-[44px]", // 44px minimum for touch targets
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  ripple?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, ripple = true, children, onClick, ...props }, ref) => {
    const [ripples, setRipples] = React.useState<Array<{ x: number; y: number; size: number; id: number }>>([]);
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    React.useImperativeHandle(ref, () => buttonRef.current!);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (ripple && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;
        const newRipple = { x, y, size, id: Date.now() };

        setRipples((prev) => [...prev, newRipple]);

        setTimeout(() => {
          setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
        }, 600);
      }

      if (onClick && !loading) {
        onClick(e);
      }
    };

    if (asChild) {
      return (
        <Slot className={cn(buttonVariants({ variant, size, className }))} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(
          buttonVariants({ variant, size, className }),
          "relative overflow-hidden",
          loading && "cursor-wait"
        )}
        ref={buttonRef}
        onClick={handleClick}
        disabled={loading || props.disabled}
        {...props}
      >
        {ripples.map((ripple) => (
          <span
            key={ripple.id}
            className="pointer-events-none absolute animate-ripple rounded-full bg-white/30"
            style={{
              left: ripple.x,
              top: ripple.y,
              width: ripple.size,
              height: ripple.size,
            }}
          />
        ))}
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        <span className={cn(loading && "opacity-70")}>{children}</span>
      </button>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
